// Pure, exported Home/Dashboard derivation logic — extracted out of
// Dashboard.jsx (2026-07) so it's directly unit-testable without
// mounting the full component (3 API clients + 5 child components,
// the same mock-surface cost that got Protocol.jsx's screen-level
// tests deferred). Straight relocation of existing behavior — no
// logic changes.
import { timeWindow, MIDDAY_PROTOCOL_ID, EVENING_PROTOCOL_ID } from "./activeProtocol";

const EVENING_DONE_KEY = "haiven.evening.v1";

// User-local YYYY-MM-DD. Backs Dashboard's evening-done marker
// specifically — DayDetail.jsx and SignalHistory.jsx keep their own
// copies of this same pattern (a cross-file dedup candidate, out of
// scope for this extraction).
export function localDateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Time-of-day state for the Home primary card, derived from the SAME
// band function the protocol router uses (activeProtocol.js
// timeWindow: morning 5–11, afternoon 11–17, evening 17–24, rest 0–5).
// Dashboard previously kept its own divergent bands (midday until
// 19:00, evening including 0–5am), which produced split-brain
// evenings: from 5–7pm the Home hero said "Midday check-in" while the
// Today's-protocol card and the Protocol tab both pushed the Evening
// ritual, and at 3am Home pushed the wind-down while the Protocol tab
// said "Rest well." One clock now; the 0–5am band gets its own "rest"
// card that mirrors the Protocol tab's rest state (with the wind-down
// still reachable for night owls — the deliberate intent of the old
// 0–5am mapping).
export function homeTimeState(hour = new Date().getHours()) {
  const w = timeWindow(hour);
  if (w === null) return "rest";
  return w === "afternoon" ? "midday" : w;
}

// "Did the Evening Wind-Down ritual complete today?" — set by
// EveningWindDown.jsx on the final step. Read locally so the Home
// evening card can reflect the done state without a network
// round-trip.
export function eveningDoneToday() {
  try { return localStorage.getItem(EVENING_DONE_KEY) === localDateKey(); }
  catch { return false; }
}

// OR-merge of local-device truth and server truth for "evening done".
// The localStorage marker alone (eveningDoneToday) is device-local: on
// a second device or after a storage clear, Home said "Start your
// evening reflection" for a ritual the server already knows is
// finished — this merge is what fixed that.
export function mergeEveningDone(localDone, serverDone) {
  return !!localDone || !!serverDone;
}

// Server-truth "evening ritual complete today" — every rendered step
// for the Evening Wind-Down protocol has a completion row.
export function eveningDoneFromServer(totalsById, countById) {
  const evTotal = totalsById?.[EVENING_PROTOCOL_ID] ?? 0;
  return evTotal > 0 && (countById?.[EVENING_PROTOCOL_ID] ?? 0) === evTotal;
}

export const REVEAL_THRESHOLD = 7;

// Gates the score ring vs. the RevealCountdown placeholder — true once
// the user has enough history to show a real score.
export function isRevealed(snapshot, threshold = REVEAL_THRESHOLD) {
  return !!snapshot && (snapshot?.total_snapshots ?? 0) >= threshold;
}

// SAFETY_GATED early-return gate for Dashboard's non-directive
// grounding view.
export function isSafetyGated(snapshot) {
  return snapshot?.coherence_status === "SAFETY_GATED";
}

// Time-aware primary card. Morning routes into the daily check-in,
// midday into the (light placeholder) Midday Clarity Reset protocol,
// evening into the Evening Wind-Down ritual launch screen; the 0–5am
// rest band mirrors the Protocol tab's "Rest well." state while
// keeping the wind-down reachable for night owls.
//
// ``protocolId`` names the protocol this card already IS the entry
// point for — the render layer suppresses the "Today's protocol" card
// (see showsTodaysProtocolCard below) when it resolves to the same
// protocol, so the same action is never stacked twice (previously
// handled for evening only; midday had the identical double-card
// problem from 11am–5pm).
export function selectPrimaryCard({ timeState, checkedInToday, checkinHref, eveningDone }) {
  return {
    morning: {
      kicker: "Morning",
      title: "Morning Check-in",
      sub: checkedInToday
        ? "You've checked in today. Revisit it anytime."
        : "A deliberate start before the day's demands arrive.",
      cta: checkedInToday ? "Check in again" : "Start your morning",
      href: checkinHref,
      protocolId: null, // the check-in isn't a protocol card
    },
    midday: {
      kicker: "Midday",
      title: "Midday check-in",
      sub: "A short reset to clear the load and recheck your intention.",
      cta: "Midday check-in",
      href: "/protocol/midday-clarity-reset",
      protocolId: MIDDAY_PROTOCOL_ID,
    },
    evening: {
      kicker: "Evening",
      title: "Evening Wind-Down",
      sub: eveningDone
        ? "Evening reflection complete. Rest well."
        : "Close the day with intention.",
      cta: eveningDone ? "Revisit" : "Start your evening reflection",
      href: "/evening",
      protocolId: EVENING_PROTOCOL_ID,
    },
    rest: {
      kicker: "Overnight",
      title: "Rest well.",
      sub: "Morning Anchor will be here when you wake up. Still up? You can close the day now.",
      cta: "Evening Wind-Down",
      href: "/evening",
      protocolId: EVENING_PROTOCOL_ID,
    },
  }[timeState];
}

// Whether the "Today's protocol" card should render — suppressed
// whenever it would duplicate the primary card's own action (evening,
// midday: protocolId already IS the primary card's action, so
// ID-equality is enough).
//
// Morning is different: the check-in and Morning Anchor are genuinely
// separate activities (a mood/reflection check-in vs. a physical
// ritual — cold water, movement, hydration, a written priority), with
// fully independent completion tracking. Neither satisfies the other,
// so ID-equality can never de-dup them, and blanket-hiding Morning
// Anchor would hide real, unrelated work. Instead we sequence them:
// keep the protocol card off the screen until check-in is done (so we
// don't stack two morning CTAs before the user has done either), then
// reveal it once check-in is complete, when it's clearly a distinct
// next task rather than a competing first one.
export function showsTodaysProtocolCard(
  nextProtocol,
  primaryCardProtocolId,
  { timeState, checkedInToday } = {}
) {
  if (!nextProtocol || nextProtocol.id === primaryCardProtocolId) return false;
  if (timeState === "morning" && !checkedInToday) return false;
  return true;
}
